import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
import React from "react";
import _ from "lodash";
import { AppEventPublisher } from "../AppEventPublisher";
import { TourIds, TourSignal, AnchorIds, Tour, TourStep, ValidTourVariations } from "../components/StickyTour/TourTypes";
import { createSafeStorage } from "./safeStorage";

type TourPhase = 'idle' | 'cleaning-up' | 'setting-up' | 'active';

type PendingTour = {
  tour: Tour;
  startAtStepId?: string;
};

type SavedTourSettings = {
  wormholeOutputLength?: number;
};

type StickyTourState = {
  currentStepIndex: number;
  anchors: Record<string, React.RefObject<HTMLDivElement>>;
  steps: TourStep[];
  setSteps: (steps: TourStep[]) => void;
  setAnchors: (anchorId: AnchorIds, ref: React.RefObject<HTMLDivElement>) => void;
  removeAnchor: (anchorId: AnchorIds) => void;
  sendFlowSignal: (signal: TourSignal) => void;
  sendFailureSignal: (signal: TourSignal) => void;
  setToStepById: (stepId: string) => void;
  nextStep: () => void;
  prevStep: () => void;
  isTourActive: boolean;
  /** Ends the current tour. Pass isEarlyExit=true for premature exits. */
  endTour: (isEarlyExit?: boolean) => void;
  startTour: (tour: Tour, options?: { startAtStepId?: string }) => void;
  activeTourId: TourIds | undefined;
  activeTourVariation?: ValidTourVariations;
  /** Whether to show anchor debug indicators */
  showAnchors: boolean;
  setShowAnchors: (show: boolean) => void;
  /** Saved settings to restore when tour ends */
  savedSettings: SavedTourSettings;
  setSavedSettings: (settings: SavedTourSettings) => void;
  clearSavedSettings: () => void;
  /** Word count of the current editor selection, updated live */
  selectedWordCount: number;
  setSelectedWordCount: (count: number) => void;
  // Phase-based state machine fields
  phase: TourPhase;
  pendingTour: PendingTour | null;
  cleaningUpTourId: TourIds | null;
  /** Called by component when async cleanup finishes */
  cleanupComplete: () => void;
  /** Called by component when async setup finishes */
  setupComplete: () => void;
};

const initialState = {
  currentStepIndex: 0,
  steps: [],
  anchors: {},
  isTourActive: false,
  activeTourId: undefined,
  activeTourVariation: undefined,
  showAnchors: false,
  savedSettings: {},
  selectedWordCount: 0,
  // Phase-based state machine fields
  phase: 'idle' as TourPhase,
  pendingTour: null,
  cleaningUpTourId: null,
};

export const useStickyTourStore = create<StickyTourState>()(
  persist(
    (set, get) => ({
      ...initialState,

      setSteps: (steps: TourStep[]) => set({ steps }),
      setAnchors: (anchorId: AnchorIds, ref: React.RefObject<HTMLDivElement>) => set({ anchors: { ...get().anchors, [anchorId]: ref } }),
      removeAnchor: (anchorId: AnchorIds) => {
        const newAnchors = { ...get().anchors };
        delete newAnchors[anchorId];
        set({ anchors: newAnchors });
      },
      sendFlowSignal: (signal: TourSignal) => {
        const { currentStepIndex, steps } = get();
        const currentStep = steps[currentStepIndex];
        if (!currentStep) return;

        const skipTargetId = currentStep.skipSignals?.[signal];
        if (skipTargetId) {
          const targetIndex = steps.findIndex(s => s.id === skipTargetId);
          if (targetIndex === -1 || targetIndex <= currentStepIndex) return;

          let next = get().steps[get().currentStepIndex];
          // We loop instead of just skip to the target step so that the user still appears as progressing through the mixpanel funnel
          while (next && next.id !== skipTargetId) {
            get().nextStep();
            next = get().steps[get().currentStepIndex];
          }
          return;
        }

        if (currentStep.listeningFor?.includes(signal)) {
          get().nextStep();
        }
      },
      sendFailureSignal: (signal: TourSignal) => {
        const { currentStepIndex, steps, activeTourId, phase } = get();
        if (!activeTourId || phase !== 'active') return;

        const currentStep = steps[currentStepIndex];

        if (currentStep && currentStep.listeningForFailure?.includes(signal)) {
          get().prevStep();
        }
      },
      nextStep: () => {
        const { currentStepIndex, steps, activeTourId, activeTourVariation, phase } = get();
        if (!activeTourId || phase !== 'active') return;

        const currentStep = steps[currentStepIndex];
        if (currentStep) {
          AppEventPublisher.onboarding.stepCompleted(activeTourId, currentStep.id, currentStepIndex, activeTourVariation);
        }

        const nextStepIndex = currentStepIndex + 1;
        if (nextStepIndex === steps.length) {
          // Tour complete - send completion analytics, then transition to cleanup
          AppEventPublisher.onboarding.completeTour(activeTourId, activeTourVariation);
          set({
            phase: 'cleaning-up',
            cleaningUpTourId: activeTourId,
          });
          return;
        }
        set({ currentStepIndex: nextStepIndex });
      },
      prevStep: () => {
        const { currentStepIndex, activeTourId, phase } = get();
        if (!activeTourId || phase !== 'active') return;

        // Don't go below 0
        if (currentStepIndex <= 0) return;

        set({ currentStepIndex: currentStepIndex - 1 });
      },
      setToStepById: (stepId: string) => {
        const steps = get().steps;
        const step = steps.find((step) => step.id === stepId);
        if (step) {
          set({ currentStepIndex: steps.indexOf(step) });
        }
      },
      startTour: (tour: Tour, options?: { startAtStepId?: string }) => {
        const { phase, activeTourId } = get();

        const pendingTour: PendingTour = {
          tour,
          startAtStepId: options?.startAtStepId,
        };

        // If we're in the middle of setting up, ignore the request
        if (phase === 'setting-up') {
          console.warn(`startTour called during setup phase. Ignoring request for tour: ${tour.id}`);
          return;
        }

        // Fire analytics immediately when tour is requested (before any queuing/transitions)
        AppEventPublisher.onboarding.startTour(tour.id, tour.variation);

        // If we're cleaning up, queue the new tour (analytics already fired above)
        if (phase === 'cleaning-up') {
          set({ pendingTour });
          return;
        }

        // If there's an active tour, we need to clean it up first
        if (activeTourId) {
          set({
            phase: 'cleaning-up',
            cleaningUpTourId: activeTourId,
            pendingTour,
          });
        } else {
          // No active tour, go straight to setup
          set({
            phase: 'setting-up',
            pendingTour,
          });
        }
      },
      endTour: (isEarlyExit?: boolean) => {
        const { activeTourId, activeTourVariation, currentStepIndex, steps, phase } = get();
        if (!activeTourId || phase !== 'active') return;

        // Send exit analytics if this is an early exit (not a successful completion)
        if (isEarlyExit) {
          const currentStep = steps[currentStepIndex];
          if (currentStep) {
            AppEventPublisher.onboarding.exitTour(activeTourId, currentStep.id, currentStepIndex, activeTourVariation);
          }
        }

        set({
          phase: 'cleaning-up',
          cleaningUpTourId: activeTourId,
        });
      },
      cleanupComplete: () => {
        const { pendingTour } = get();

        // Clear active tour state
        set({
          activeTourId: undefined,
          activeTourVariation: undefined,
          cleaningUpTourId: null,
          isTourActive: false,
        });

        if (pendingTour) {
          // Move to setup phase for next tour
          set({ phase: 'setting-up' });
        } else {
          // No pending tour, go to idle
          set({
            phase: 'idle',
            steps: [],
            currentStepIndex: 0,
          });
        }
      },
      setupComplete: () => {
        const { pendingTour } = get();
        if (!pendingTour) {
          set({
            phase: 'idle',
            steps: [],
            currentStepIndex: 0,
          });
          return;
        }

        const { tour, startAtStepId } = pendingTour;

        // Determine starting index
        let startIndex = 0;
        if (startAtStepId) {
          const idx = tour.steps.findIndex(s => s.id === startAtStepId);
          if (idx !== -1) startIndex = idx;
        }

        // Note: startTour analytics were already fired in startTour()
        set({
          phase: 'active',
          activeTourId: tour.id,
          activeTourVariation: tour.variation,
          steps: tour.steps,
          currentStepIndex: startIndex,
          isTourActive: true,
          pendingTour: null,
        });
      },
      setShowAnchors: (show: boolean) => set({ showAnchors: show }),
      setSavedSettings: (settings: SavedTourSettings) => set({ savedSettings: { ...get().savedSettings, ...settings } }),
      clearSavedSettings: () => set({ savedSettings: {} }),
      setSelectedWordCount: (count: number) => set({ selectedWordCount: count }),
    }),
    {
      name: "sticky-tour-store",
      storage: createJSONStorage(() => createSafeStorage(typeof window !== "undefined" ? window.localStorage : null)),
      partialize: (state) => ({
        currentStepIndex: state.currentStepIndex,
        activeTourId: state.activeTourId,
        activeTourVariation: state.activeTourVariation,
        isTourActive: state.isTourActive,
        phase: state.phase,
        pendingTour: state.pendingTour,
        cleaningUpTourId: state.cleaningUpTourId,
        savedSettings: state.savedSettings,
      }),
    }
  )
);

export const withStickyTourStore = (BaseComponent: any) => {
  const MemoizedBaseComponent = React.memo(BaseComponent, (prev, next) => {
    return _.isEqual(prev, next);
  });

  return (props: any) => {
    const selectedState = useStickyTourStore((state) => state);

    return React.createElement(MemoizedBaseComponent, {
      ...props,
      stickyTourStore: selectedState,
      sendFlowSignal: selectedState.sendFlowSignal,
      sendFailureSignal: selectedState.sendFailureSignal,
    });
  };
};
